import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteProjectVehicleKmRecord,
  fetchProjectVehicleKmRecords,
  saveProjectVehicleKmRecord,
  updateProjectVehicleKmRecord,
} from "../api";
import { getApiErrorMessage } from "../api/client";
import {
  exportVehicleRowsToExcel,
  toExcelNumber,
} from "../utils/vehicleExcelExport";

function getTodayDateValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateLabel(dateValue) {
  if (!dateValue) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(`${dateValue}T00:00:00`));
}

function toKmNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function getRecordKmBoundary(record, mode) {
  const values = [toKmNumber(record?.departureKm), toKmNumber(record?.returnKm)]
    .filter((value) => value != null);
  if (values.length === 0) return null;
  return mode === "next" ? Math.min(...values) : Math.max(...values);
}

export default function VehicleKmDriverPage({
  title,
  onBack,
  canDeleteRecords,
  currentUserId,
  currentUserName,
  selectedProjectId,
  isOnline,
}) {
  const todayDate = getTodayDateValue();
  const [vehicles, setVehicles] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveNotice, setSaveNotice] = useState("");
  const [saveNoticeKind, setSaveNoticeKind] = useState("");
  const [editRecord, setEditRecord] = useState(null);
  const [selectedLogDate, setSelectedLogDate] = useState(todayDate);
  const [exportStartDate, setExportStartDate] = useState(todayDate);
  const [exportEndDate, setExportEndDate] = useState(todayDate);
  const [exportNotice, setExportNotice] = useState("");

  const loadVehicleKmWorkspace = useCallback(async () => {
    if (!selectedProjectId) return;
    if (!isOnline) {
      setSaveNotice("Çevrimdışı modda KM zinciri değiştirilemez. Bağlantı gelince tekrar deneyin.");
      setSaveNoticeKind("info");
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchProjectVehicleKmRecords(selectedProjectId);
      const nextVehicles = Array.isArray(data?.vehicles) ? data.vehicles : [];
      const nextRecords = Array.isArray(data?.records) ? data.records : [];
      const activeVehicle = nextVehicles.find((item) => item.active !== false);
      const fallbackVehicle = activeVehicle || nextVehicles[0] || null;

      setVehicles(nextVehicles);
      setRecords(
        nextRecords.map((record) => ({
          ...record,
          persistedReturnKm: record.returnKm,
        }))
      );
      setSelectedVehicleId((previous) => {
        if (nextVehicles.some((item) => item.id === previous)) {
          return previous;
        }
        return fallbackVehicle?.id || null;
      });
      setSaveNotice("");
      setSaveNoticeKind("");
      setExportNotice("");
      setSelectedLogDate(todayDate);
      setExportStartDate(todayDate);
      setExportEndDate(todayDate);
    } catch (error) {
      setSaveNotice(getApiErrorMessage(error, "Araç km kayıtları yüklenemedi."));
      setSaveNoticeKind("error");
    } finally {
      setIsLoading(false);
    }
  }, [isOnline, selectedProjectId, todayDate]);

  useEffect(() => {
    loadVehicleKmWorkspace();
  }, [loadVehicleKmWorkspace]);

  const activeVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicle.active !== false),
    [vehicles]
  );

  const archivedVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicle.active === false),
    [vehicles]
  );

  const selectedVehicle = useMemo(
    () => vehicles.find((item) => item.id === selectedVehicleId) || null,
    [selectedVehicleId, vehicles]
  );

  const selectedVehicleRecords = useMemo(
    () =>
      records
        .filter((record) => record.vehicleId === selectedVehicleId)
        .sort((left, right) => right.date.localeCompare(left.date)),
    [records, selectedVehicleId]
  );

  const selectedVehicleOpenTrip = useMemo(
    () =>
      [...selectedVehicleRecords].reverse().find(
        (record) =>
          !String(record.id || "").startsWith("draft-") &&
          toKmNumber(record.departureKm) != null &&
          toKmNumber(record.persistedReturnKm) == null
      ) || null,
    [selectedVehicleRecords]
  );

  useEffect(() => {
    setSelectedLogDate(selectedVehicleOpenTrip?.date || todayDate);
  }, [selectedVehicleId, selectedVehicleOpenTrip?.date, todayDate]);

  const selectedLog = useMemo(
    () =>
      selectedVehicleRecords.find((record) => record.date === selectedLogDate) || {
        date: selectedLogDate,
        driverName: currentUserName,
        departureKm: "",
        returnKm: "",
      },
    [currentUserName, selectedLogDate, selectedVehicleRecords]
  );

  const isReturnEntry = Boolean(selectedVehicleOpenTrip);
  const selectedLogIsCompleted = Boolean(
    !String(selectedLog.id || "").startsWith("draft-") &&
      toKmNumber(selectedLog.persistedReturnKm ?? selectedLog.returnKm) != null
  );

  const handleKmChange = useCallback((field, value) => {
    const normalizedValue = String(value || "").replace(/[^\d]/g, "");
    setRecords((previous) => {
      const existingIndex = previous.findIndex(
        (item) => item.vehicleId === selectedVehicleId && item.date === selectedLogDate
      );

      if (existingIndex > -1) {
        return previous.map((item, index) =>
          index === existingIndex
            ? { ...item, [field]: normalizedValue, driverName: currentUserName }
            : item
        );
      }

      return [
        {
          id: `draft-${selectedVehicleId}-${selectedLogDate}`,
          vehicleId: selectedVehicleId,
          projectId: selectedProjectId,
          date: selectedLogDate,
          driverName: currentUserName,
          departureKm: field === "departureKm" ? normalizedValue : "",
          returnKm: field === "returnKm" ? normalizedValue : "",
        },
        ...previous,
      ];
    });
    setSaveNotice("");
    setSaveNoticeKind("");
  }, [currentUserName, selectedLogDate, selectedProjectId, selectedVehicleId]);

  const handleSaveLog = useCallback(async () => {
    if (!isOnline) {
      setSaveNotice("KM sırası sunucuda doğrulandığı için bu işlem bağlantı gerektirir.");
      setSaveNoticeKind("error");
      return;
    }
    if (!selectedVehicleId || !selectedVehicle) return;
    if (!selectedLogDate) {
      setSaveNotice("Kayıt tarihi seçin.");
      setSaveNoticeKind("error");
      return;
    }
    if (selectedVehicle.active === false) {
      setSaveNotice("Pasif araç için yeni km kaydı açılamaz.");
      setSaveNoticeKind("error");
      return;
    }
    if (selectedLogIsCompleted && !isReturnEntry) {
      setSaveNotice("Bu tarihteki çıkış ve dönüş kaydı tamamlanmış.");
      setSaveNoticeKind("error");
      return;
    }

    const effectiveLogDate = selectedVehicleOpenTrip?.date || selectedLogDate;
    const departureKm = toKmNumber(
      selectedVehicleOpenTrip?.departureKm ?? selectedLog.departureKm
    );
    const returnKm = toKmNumber(selectedLog.returnKm);
    if (departureKm == null) {
      setSaveNotice("Çıkış km zorunludur.");
      setSaveNoticeKind("error");
      return;
    }
    if (!isReturnEntry && returnKm != null) {
      setSaveNotice("Önce yalnız çıkış km değerini kaydedin.");
      setSaveNoticeKind("error");
      return;
    }
    if (isReturnEntry && returnKm == null) {
      setSaveNotice("Açık çıkışı tamamlamak için dönüş km değerini girin.");
      setSaveNoticeKind("error");
      return;
    }
    if (isReturnEntry && returnKm <= departureKm) {
      setSaveNotice("Dönüş km, çıkış km değerinden büyük olmalıdır.");
      setSaveNoticeKind("error");
      return;
    }

    const persistedOtherRecords = selectedVehicleRecords.filter(
      (record) =>
        record.date !== effectiveLogDate &&
        !String(record.id || "").startsWith("draft-")
    );
    const previousRecord = persistedOtherRecords.find(
      (record) => record.date < effectiveLogDate
    );
    const nextRecord = [...persistedOtherRecords]
      .reverse()
      .find((record) => record.date > effectiveLogDate);
    const previousKm = getRecordKmBoundary(previousRecord, "previous");
    if (previousKm != null && departureKm <= previousKm) {
      setSaveNotice(
        `Çıkış km, önceki kayıttaki ${previousKm} km değerinden büyük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    const currentLastKm = returnKm == null ? departureKm : returnKm;
    const nextKm = getRecordKmBoundary(nextRecord, "next");
    if (nextKm != null && currentLastKm >= nextKm) {
      setSaveNotice(
        `Girilen son km, sonraki kayıttaki ${nextKm} km değerinden küçük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    try {
      const savedRecord = await saveProjectVehicleKmRecord(selectedProjectId, {
        vehicleId: selectedVehicleId,
        date: effectiveLogDate,
        departureKm,
        returnKm: isReturnEntry ? returnKm : "",
      });

      setRecords((previous) => {
        const withoutCurrent = previous.filter(
          (item) =>
            !(
              item.vehicleId === selectedVehicleId &&
              item.date === effectiveLogDate
            )
        );
        return [
          { ...savedRecord, persistedReturnKm: savedRecord.returnKm },
          ...withoutCurrent,
        ];
      });
      if (isReturnEntry) {
        setSelectedLogDate(todayDate);
        setSaveNotice("Dönüş kaydedildi; kayıt çıkış tarihinde tamamlandı.");
      } else {
        setSaveNotice("Çıkış kaydedildi. Dönüşü daha sonra aynı kayda girebilirsiniz.");
      }
      setSaveNoticeKind("success");
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, "Km kaydı kaydedilemedi.");
      await loadVehicleKmWorkspace();
      setSaveNotice(errorMessage);
      setSaveNoticeKind("error");
    }
  }, [
    isOnline,
    loadVehicleKmWorkspace,
    selectedProjectId,
    selectedLog.departureKm,
    selectedLog.returnKm,
    selectedLogDate,
    selectedLogIsCompleted,
    selectedVehicle,
    selectedVehicleOpenTrip,
    selectedVehicleId,
    selectedVehicleRecords,
    isReturnEntry,
    todayDate,
  ]);

  const handleStartRecordEdit = useCallback((record) => {
    setEditRecord({
      id: record.id,
      departureKm: record.departureKm,
      returnKm: record.persistedReturnKm ?? record.returnKm,
    });
    setSaveNotice("");
    setSaveNoticeKind("");
  }, []);

  const handleSaveRecordEdit = useCallback(async () => {
    if (!isOnline) {
      setSaveNotice("KM düzeltmesi bağlantı gerektirir.");
      setSaveNoticeKind("error");
      return;
    }
    if (!editRecord) return;
    const currentRecord = selectedVehicleRecords.find(
      (record) => record.id === editRecord.id
    );
    if (!currentRecord) return;

    const departureKm = toKmNumber(editRecord.departureKm);
    const returnKm = toKmNumber(editRecord.returnKm);
    if (departureKm == null) {
      setSaveNotice("Çıkış km zorunludur.");
      setSaveNoticeKind("error");
      return;
    }
    if (toKmNumber(currentRecord.persistedReturnKm ?? currentRecord.returnKm) != null && returnKm == null) {
      setSaveNotice("Tamamlanmış kayıtta dönüş km boş bırakılamaz.");
      setSaveNoticeKind("error");
      return;
    }
    if (returnKm != null && returnKm <= departureKm) {
      setSaveNotice("Dönüş km, çıkış km değerinden büyük olmalıdır.");
      setSaveNoticeKind("error");
      return;
    }

    const otherRecords = selectedVehicleRecords.filter(
      (record) =>
        record.id !== currentRecord.id &&
        !String(record.id || "").startsWith("draft-")
    );
    const previousRecord = otherRecords.find(
      (record) => record.date < currentRecord.date
    );
    const nextRecord = [...otherRecords]
      .reverse()
      .find((record) => record.date > currentRecord.date);
    const previousKm = getRecordKmBoundary(previousRecord, "previous");
    if (previousKm != null && departureKm <= previousKm) {
      setSaveNotice(
        `Çıkış km, önceki kayıttaki ${previousKm} km değerinden büyük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }
    const lastKm = returnKm == null ? departureKm : returnKm;
    const nextKm = getRecordKmBoundary(nextRecord, "next");
    if (nextKm != null && lastKm >= nextKm) {
      setSaveNotice(
        `Girilen son km, sonraki kayıttaki ${nextKm} km değerinden küçük olmalıdır.`
      );
      setSaveNoticeKind("error");
      return;
    }

    setIsLoading(true);
    try {
      const updatedRecord = await updateProjectVehicleKmRecord(
        selectedProjectId,
        currentRecord.id,
        {
          departureKm,
          returnKm: returnKm == null ? "" : returnKm,
        }
      );
      setRecords((previous) =>
        previous.map((record) =>
          record.id === currentRecord.id
            ? { ...updatedRecord, persistedReturnKm: updatedRecord.returnKm }
            : record
        )
      );
      setEditRecord(null);
      setSaveNotice("KM sayıları düzeltildi.");
      setSaveNoticeKind("success");
    } catch (error) {
      setSaveNotice(getApiErrorMessage(error, "KM sayıları düzeltilemedi."));
      setSaveNoticeKind("error");
    } finally {
      setIsLoading(false);
    }
  }, [editRecord, isOnline, selectedProjectId, selectedVehicleRecords]);

  const handleDeleteRecord = useCallback(async (record) => {
    if (!isOnline) {
      setSaveNotice("KM kaydı silme işlemi bağlantı gerektirir.");
      setSaveNoticeKind("error");
      return;
    }
    if (!canDeleteRecords) return;
    const confirmed = window.confirm(
      `${formatDateLabel(record.date)} tarihli KM kaydı silinsin mi? Bu işlem geri alınamaz.`
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await deleteProjectVehicleKmRecord(selectedProjectId, record.id);
      setRecords((previous) =>
        previous.filter((item) => item.id !== record.id)
      );
      setEditRecord((previous) => (previous?.id === record.id ? null : previous));
      setSaveNotice("KM kaydı silindi.");
      setSaveNoticeKind("success");
    } catch (error) {
      setSaveNotice(getApiErrorMessage(error, "KM kaydı silinemedi."));
      setSaveNoticeKind("error");
    } finally {
      setIsLoading(false);
    }
  }, [canDeleteRecords, isOnline, selectedProjectId]);

  const handleDownloadByDate = useCallback(async () => {
    setExportNotice("");

    if (!exportStartDate || !exportEndDate) {
      setExportNotice("İndirme için başlangıç ve bitiş tarihi seçin.");
      return;
    }

    if (exportStartDate > exportEndDate) {
      setExportNotice("Başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }

    const filteredRows = records
      .filter(
        (row) =>
          !String(row.id || "").startsWith("draft-") &&
          row.date >= exportStartDate &&
          row.date <= exportEndDate
      )
      .map((row) => ({
        ...row,
        plate: vehicles.find((vehicle) => vehicle.id === row.vehicleId)?.plate || "-",
      }))
      .sort((left, right) => {
        if (left.date !== right.date) {
          return left.date.localeCompare(right.date);
        }
        return left.plate.localeCompare(right.plate, "tr");
      });

    if (filteredRows.length === 0) {
      setExportNotice("Seçilen tarih aralığında indirilecek kayıt yok.");
      return;
    }

    try {
      await exportVehicleRowsToExcel({
        fileName: `arac-km-kayitlari_${exportStartDate}_${exportEndDate}.xlsx`,
        worksheetName: "Araç KM Kayıtları",
        columns: [
          { header: "Tarih", key: "date", width: 14 },
          { header: "Plaka", key: "plate", width: 18 },
          { header: "Sürücü", key: "driverName", width: 24 },
          { header: "Çıkış Km", key: "departureKm", width: 15 },
          { header: "Dönüş Km", key: "returnKm", width: 15 },
        ],
        rows: filteredRows.map((row) => ({
          date: formatDateLabel(row.date),
          plate: row.plate,
          driverName: row.driverName || "-",
          departureKm: toExcelNumber(row.departureKm),
          returnKm: toExcelNumber(row.returnKm),
        })),
      });
      setExportNotice(`${filteredRows.length} kayıt Excel olarak indirildi.`);
    } catch (error) {
      setExportNotice(getApiErrorMessage(error, "Excel dosyası oluşturulamadı."));
    }
  }, [exportEndDate, exportStartDate, records, vehicles]);

  return (
    <div className="tool-workspace tool-workspace--vehicle">
      <div className="tool-workspace__panel">
        <div className="tool-workspace__header">
          <div>
            <div className="tool-workspace__eyebrow">Araç Modülü</div>
            <h1 className="tool-workspace__title">{title}</h1>
            <p className="tool-workspace__description">
              Araç havuzu proje bazlıdır. Aktif araçlar yeni kayıt için
              seçilebilir, pasife alınan araçların eski kayıtları ise görünmeye
              devam eder.
            </p>
          </div>

          <button
            type="button"
            className="tool-workspace__back-button"
            onClick={onBack}
          >
            Haritaya Dön
          </button>
        </div>

        {!isOnline ? (
          <div className="vehicle-km-page__helper-text vehicle-km-page__save-notice vehicle-km-page__save-notice--info">
            Çevrimdışı: saha noktaları ve ölçümler saklanır; KM zinciri bağlantı gelene kadar salt okunurdur.
          </div>
        ) : null}

        <div className="vehicle-km-page">
          <aside className="vehicle-km-page__sidebar">
            <div className="vehicle-km-page__sidebar-header">
              <h2 className="vehicle-km-page__sidebar-title">Aktif Araçlar</h2>
              <p className="vehicle-km-page__sidebar-text">
                Seçtiğiniz tarihte km kaydı için sadece bu projeye bağlı aktif araçlar
                seçilir. Araç değişimi admin tarafından yönetilir.
              </p>
            </div>

            <div className="vehicle-km-page__plate-list">
              {activeVehicles.length > 0 ? (
                activeVehicles.map((vehicle) => (
                  <button
                    key={vehicle.id}
                    type="button"
                    className={`vehicle-km-page__plate-button ${
                      selectedVehicleId === vehicle.id
                        ? "vehicle-km-page__plate-button--active"
                        : ""
                    }`.trim()}
                    onClick={() => setSelectedVehicleId(vehicle.id)}
                  >
                    <span className="vehicle-km-page__plate-value">
                      {vehicle.plate}
                    </span>
                    <span className="vehicle-km-page__plate-meta">
                      {
                        records.filter((record) => record.vehicleId === vehicle.id)
                          .length
                      }{" "}
                      günlük kayıt
                    </span>
                  </button>
                ))
              ) : (
                <div className="vehicle-km-page__empty-state">
                  Bu proje için aktif araç yok. Araç havuzu admin tarafından
                  tanımlanmalıdır.
                </div>
              )}
            </div>

            <div className="vehicle-km-page__export-panel">
              <div className="vehicle-km-page__export-header">
                <h3 className="vehicle-km-page__export-title">Toplu İndir</h3>
                <div className="vehicle-km-page__export-count">
                  {records.length} kayıt
                </div>
              </div>

              <div className="vehicle-km-page__export-grid">
                <div className="vehicle-km-page__field">
                  <label className="vehicle-km-page__field-label" htmlFor="vehicle-export-start-date">
                    Başlangıç Tarihi
                  </label>
                  <input
                    id="vehicle-export-start-date"
                    type="date"
                    className="vehicle-km-page__text-input"
                    value={exportStartDate}
                    onChange={(event) => {
                      setExportStartDate(event.target.value);
                      setExportNotice("");
                    }}
                  />
                </div>

                <div className="vehicle-km-page__field">
                  <label className="vehicle-km-page__field-label" htmlFor="vehicle-export-end-date">
                    Bitiş Tarihi
                  </label>
                  <input
                    id="vehicle-export-end-date"
                    type="date"
                    className="vehicle-km-page__text-input"
                    value={exportEndDate}
                    onChange={(event) => {
                      setExportEndDate(event.target.value);
                      setExportNotice("");
                    }}
                  />
                </div>
              </div>

              <button
                type="button"
                className="vehicle-km-page__primary-button vehicle-km-page__export-button"
                onClick={handleDownloadByDate}
              >
                Tarihe Göre Excel İndir
              </button>

              {exportNotice ? (
                <div className="vehicle-km-page__helper-text">{exportNotice}</div>
              ) : null}
            </div>

            {archivedVehicles.length > 0 ? (
              <div className="vehicle-km-page__archive-panel">
                <div className="vehicle-km-page__sidebar-header">
                  <h3 className="vehicle-km-page__export-title">Geçmiş Araçlar</h3>
                  <p className="vehicle-km-page__sidebar-text">
                    Pasife alınan araçların eski kayıtları burada görüntülenir.
                  </p>
                </div>
                <div className="vehicle-km-page__plate-list">
                  {archivedVehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      type="button"
                      className={`vehicle-km-page__plate-button vehicle-km-page__plate-button--archived ${
                        selectedVehicleId === vehicle.id
                          ? "vehicle-km-page__plate-button--active"
                          : ""
                      }`.trim()}
                      onClick={() => setSelectedVehicleId(vehicle.id)}
                    >
                      <span className="vehicle-km-page__plate-value">{vehicle.plate}</span>
                      <span className="vehicle-km-page__plate-meta">
                        Pasif araç
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </aside>

          <section className="vehicle-km-page__content">
            {selectedVehicle ? (
              <>
                <div className="vehicle-km-page__content-header">
                  <div>
                    <div className="vehicle-km-page__content-eyebrow">Günlük Kayıt</div>
                    <h2 className="vehicle-km-page__content-title">{selectedVehicle.plate}</h2>
                  </div>
                  <div className="vehicle-km-page__content-note">
                    {selectedVehicle.active === false
                      ? "Bu araç pasif. Eski kayıtlar görünür, yeni kayıt açılamaz."
                      : isReturnEntry
                        ? `Açık çıkış ${formatDateLabel(selectedVehicleOpenTrip.date)} tarihli. Dönüş bu tarihe kaydedilecek.`
                        : "Önce çıkış kaydedilir; dönüş daha sonra aynı tarihli kayda eklenir."}
                  </div>
                </div>

                <div className="vehicle-km-page__info-grid">
                  <div className="vehicle-km-page__field">
                    <label className="vehicle-km-page__field-label">Sürücü</label>
                    <div className="vehicle-km-page__readonly-field">
                      {isReturnEntry
                        ? selectedVehicleOpenTrip.driverName || currentUserName
                        : currentUserName}
                    </div>
                  </div>

                  <div className="vehicle-km-page__field">
                    <label className="vehicle-km-page__field-label" htmlFor="vehicle-log-date-input">
                      Tarih
                    </label>
                    <input
                      id="vehicle-log-date-input"
                      type="date"
                      className="vehicle-km-page__text-input vehicle-km-page__text-input--date"
                      value={selectedLogDate}
                      required
                      onChange={(event) => {
                        setSelectedLogDate(event.target.value);
                        setSaveNotice("");
                        setSaveNoticeKind("");
                      }}
                      disabled={selectedVehicle.active === false || isLoading || !isOnline || isReturnEntry}
                    />
                  </div>

                  <div className="vehicle-km-page__field">
                    <label className="vehicle-km-page__field-label" htmlFor="departure-km-input">
                      Çıkış Km
                    </label>
                    <input
                      id="departure-km-input"
                      type="text"
                      inputMode="numeric"
                      className="vehicle-km-page__text-input"
                      value={selectedLog.departureKm}
                      onChange={(event) => handleKmChange("departureKm", event.target.value)}
                      placeholder="Örn. 125430"
                      disabled={
                        !isOnline ||
                        selectedVehicle.active === false ||
                        isLoading ||
                        isReturnEntry ||
                        selectedLogIsCompleted
                      }
                    />
                  </div>

                  <div className="vehicle-km-page__field">
                    <label className="vehicle-km-page__field-label" htmlFor="return-km-input">
                      Dönüş Km
                    </label>
                    <input
                      id="return-km-input"
                      type="text"
                      inputMode="numeric"
                      className="vehicle-km-page__text-input"
                      value={selectedLog.returnKm}
                      onChange={(event) => handleKmChange("returnKm", event.target.value)}
                      placeholder={isReturnEntry ? "Örn. 125580" : "Önce çıkışı kaydedin"}
                      disabled={
                        !isOnline ||
                        selectedVehicle.active === false ||
                        isLoading ||
                        !isReturnEntry ||
                        selectedLogIsCompleted
                      }
                    />
                  </div>
                </div>

                <div className="vehicle-km-page__actions">
                  <button
                    type="button"
                    className="vehicle-km-page__primary-button vehicle-km-page__save-button"
                    onClick={handleSaveLog}
                    disabled={
                      !isOnline ||
                      selectedVehicle.active === false ||
                      isLoading ||
                      (selectedLogIsCompleted && !isReturnEntry)
                    }
                  >
                    {isReturnEntry ? "Dönüşü Kaydet" : "Çıkışı Kaydet"}
                  </button>
                  {saveNotice ? (
                    <div
                      className={`vehicle-km-page__helper-text vehicle-km-page__save-notice vehicle-km-page__save-notice--${saveNoticeKind || "info"}`}
                    >
                      {saveNotice}
                    </div>
                  ) : null}
                </div>

                <div className="vehicle-km-page__records">
                  <div className="vehicle-km-page__records-header">
                    <h3 className="vehicle-km-page__records-title">Kayıt Geçmişi</h3>
                    <span className="vehicle-km-page__records-badge">
                      {selectedVehicleRecords.length} gün
                    </span>
                  </div>

                  {selectedVehicleRecords.length > 0 ? (
                    <div className="vehicle-km-page__record-list">
                      {selectedVehicleRecords.map((log) => {
                        const canEditRecord =
                          !String(log.id || "").startsWith("draft-") &&
                          String(log.createdByUserId || log.driverUserId || "") ===
                            String(currentUserId || "");
                        const isEditingRecord = editRecord?.id === log.id;

                        return (
                        <div key={log.id} className="vehicle-km-page__record-row">
                          <div className="vehicle-km-page__record-cell">
                            <span className="vehicle-km-page__record-label">Tarih</span>
                            <strong>{formatDateLabel(log.date)}</strong>
                          </div>
                          <div className="vehicle-km-page__record-cell">
                            <span className="vehicle-km-page__record-label">Sürücü</span>
                            <strong>{log.driverName || "-"}</strong>
                          </div>
                          <div className="vehicle-km-page__record-cell">
                            <span className="vehicle-km-page__record-label">Çıkış Km</span>
                            {isEditingRecord ? (
                              <input
                                className="vehicle-record-edit-input"
                                inputMode="numeric"
                                value={editRecord.departureKm}
                                disabled={!isOnline}
                                onChange={(event) =>
                                  setEditRecord((previous) => ({
                                    ...previous,
                                    departureKm: event.target.value.replace(/[^\d]/g, ""),
                                  }))
                                }
                              />
                            ) : (
                              <strong>{log.departureKm || "-"}</strong>
                            )}
                          </div>
                          <div className="vehicle-km-page__record-cell">
                            <span className="vehicle-km-page__record-label">Dönüş Km</span>
                            {isEditingRecord ? (
                              <input
                                className="vehicle-record-edit-input"
                                inputMode="numeric"
                                value={editRecord.returnKm}
                                disabled={!isOnline}
                                onChange={(event) =>
                                  setEditRecord((previous) => ({
                                    ...previous,
                                    returnKm: event.target.value.replace(/[^\d]/g, ""),
                                  }))
                                }
                              />
                            ) : (
                              <strong>{log.returnKm || "-"}</strong>
                            )}
                          </div>
                          {canEditRecord || canDeleteRecords ? (
                            <div className="vehicle-km-page__record-cell vehicle-record-edit-actions">
                              <span className="vehicle-km-page__record-label">İşlem</span>
                              {isEditingRecord ? (
                                <div className="vehicle-record-edit-button-row">
                                  <button
                                    type="button"
                                    className="vehicle-record-edit-button vehicle-record-edit-button--save"
                                    onClick={handleSaveRecordEdit}
                                    disabled={isLoading || !isOnline}
                                  >
                                    Kaydet
                                  </button>
                                  <button
                                    type="button"
                                    className="vehicle-record-edit-button"
                                    onClick={() => setEditRecord(null)}
                                    disabled={isLoading || !isOnline}
                                  >
                                    İptal
                                  </button>
                                </div>
                              ) : (
                                <div className="vehicle-record-edit-button-row">
                                  {canEditRecord ? (
                                    <button
                                      type="button"
                                      className="vehicle-record-edit-button"
                                      onClick={() => handleStartRecordEdit(log)}
                                      disabled={!isOnline}
                                    >
                                      Düzenle
                                    </button>
                                  ) : null}
                                  {canDeleteRecords ? (
                                    <button
                                      type="button"
                                      className="vehicle-record-edit-button vehicle-record-edit-button--delete"
                                      onClick={() => handleDeleteRecord(log)}
                                      disabled={isLoading || !isOnline}
                                    >
                                      Sil
                                    </button>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="vehicle-km-page__empty-state vehicle-km-page__empty-state--inline">
                      Bu araç için henüz günlük kayıt yok.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="vehicle-km-page__empty-state vehicle-km-page__empty-state--content">
                Km kaydı girmek için soldan bir araç seçin.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
